import styled from "styled-components"
import colors from "~constants/colors"
import {
  REVIEW_REASON_COPY,
  REVIEW_REASON_FALLBACK_COPY,
} from "~constants/proofOfOwnership"

export interface SuggestedHolding {
  label: string
  quantity: number
}

interface AIValidationSuggestionBoxProps {
  onAcceptSuggestion: () => void
  isProcessing: boolean
  // Disabled once the operator edits the form away from the suggested values.
  disabled?: boolean
  // The pre-filled holdings, shown inline so the operator sees what was
  // extracted without scrolling down to the Holdings section.
  holdings?: SuggestedHolding[]
  // A data-bearing review_reason (LOW_CONFIDENCE, ENTITY_HOLDER,
  // ESCALATION_UNAVAILABLE, ...) explaining what to double-check before
  // accepting. Shown inline in this same green box — never a separate note
  // box — so there's always exactly one box per document.
  reviewReason?: string
}

// How many holdings to spell out before collapsing the rest into "+N more".
const MAX_HOLDINGS_SHOWN = 6

// Single green box. The scalar fields (name/broker/date) aren't repeated —
// they're already pre-filled and highlighted in the form — but the holdings
// are surfaced here as chips so the operator can eyeball them without
// scrolling. When a review_reason rides along, its copy replaces the default
// title so the "what to double-check" note lives in this same box instead of
// a separate one stacked above it.
const AIValidationSuggestionBox = ({
  onAcceptSuggestion,
  isProcessing,
  disabled = false,
  holdings = [],
  reviewReason,
}: AIValidationSuggestionBoxProps) => {
  const shown = holdings.slice(0, MAX_HOLDINGS_SHOWN)
  const extra = holdings.length - shown.length
  const copy = reviewReason
    ? REVIEW_REASON_COPY[reviewReason] || REVIEW_REASON_FALLBACK_COPY
    : null

  return (
    <Container>
      <IconCircle />
      {copy && (
        <NoteText title={copy}>
          <NoteIcon aria-hidden>⚠️</NoteIcon> {copy}
        </NoteText>
      )}
      <ActionButton
        onClick={onAcceptSuggestion}
        disabled={isProcessing || disabled}
        title={
          disabled
            ? "Revert your edits to the suggested values to accept"
            : undefined
        }
      >
        {isProcessing ? "Processing..." : "Accept & Apply All"}
      </ActionButton>
      {disabled && !isProcessing ? (
        <EditedHint>Edited — revert to accept</EditedHint>
      ) : holdings.length > 0 ? (
        <Holdings>
          <HoldingsLabel>Pre-filled</HoldingsLabel>
          {shown.map(h => (
            <Chip key={h.label}>
              <ChipCode>{h.label}</ChipCode>
              {h.quantity.toLocaleString()}
            </Chip>
          ))}
          {extra > 0 && <More>+{extra} more</More>}
        </Holdings>
      ) : null}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
`

const IconCircle = styled.div`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${colors.green};
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    top: 5px;
    left: 8px;
    transform: rotate(45deg);
  }
`

// Amber note rendered inline inside the green box (not a separate box) so a
// data-bearing review_reason (LOW_CONFIDENCE, ENTITY_HOLDER, ...) reads as a
// caution distinct from the plain green "ready to approve" title. Truncated
// to one line (full text on hover) so the box stays the same compact height
// regardless of copy length.
const NoteIcon = styled.span`
  font-size: 12px;
  line-height: 1;
`

const NoteText = styled.div`
  flex: 0 1 auto;
  min-width: 80px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 500;
  color: #92400e;
`

const ActionButton = styled.button`
  flex-shrink: 0;
  background: ${colors.green};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 0.15s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Holdings = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;
`

const HoldingsLabel = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #15803d;
`

const Chip = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 500;
  color: #14532d;
`

const ChipCode = styled.span`
  font-weight: 700;
  color: #166534;
`

const More = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: #15803d;
`

const EditedHint = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-weight: 500;
  color: #166534;
`

export default AIValidationSuggestionBox
